'use client';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useRef, type CSSProperties, type TouchEvent} from 'react';
import {flushSync} from 'react-dom';

const AXIS_LOCK_PX = 8;
const COMMIT_DISTANCE_RATIO = 0.2;
const COMMIT_VELOCITY = 0.35;
const EDGE_RESISTANCE = 0.3;
const MIN_DURATION_MS = 220;
const MAX_DURATION_MS = 420;
const ARROW_DURATION_MS = 360;
const SWIPE_CLICK_WINDOW_MS = 400;
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

export type SwipeDirection = 'prev' | 'next';

export interface UseSwipeTrackParams {
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  enabled?: boolean;
}

interface Gesture {
  startX: number;
  startY: number;
  lastX: number;
  lastTime: number;
  velocity: number;
  axis: 'x' | 'y' | null;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

function currentOffset(node: HTMLElement) {
  const transform = getComputedStyle(node).transform;
  return transform === 'none' ? 0 : new DOMMatrixReadOnly(transform).m41;
}

export function useSwipeTrack({canPrev, canNext, onPrev, onNext, enabled = true}: UseSwipeTrackParams) {
  const isRtl = useTheme().direction === 'rtl';
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const trackRef = useRef<HTMLDivElement>(null);
  const gesture = useRef<Gesture | null>(null);
  const settling = useRef(false);
  const swipedAt = useRef(0);

  const place = (node: HTMLElement, offset: number, duration: number) => {
    node.style.transition = duration > 0 ? `transform ${duration}ms ${EASING}` : 'none';
    node.style.transform = offset === 0 ? '' : `translate3d(${offset}px, 0, 0)`;
  };

  const settle = (target: number, speed: number, commit?: () => void) => {
    const node = trackRef.current;
    if (!node) {
      commit?.();
      return;
    }
    const distance = Math.abs(target - currentOffset(node));
    const motionDuration = speed > 0 ? clamp(distance / speed, MIN_DURATION_MS, MAX_DURATION_MS) : ARROW_DURATION_MS;
    const duration = reducedMotion || distance < 1 ? 0 : motionDuration;
    const finish = () => {
      if (commit) {
        flushSync(commit);
        place(node, 0, 0);
      }
      settling.current = false;
    };
    place(node, target, duration);
    if (duration === 0) {
      finish();
      return;
    }
    settling.current = true;
    window.setTimeout(finish, duration);
  };

  const nextSign = isRtl ? 1 : -1;

  const go = (direction: SwipeDirection) => {
    const node = trackRef.current;
    if (settling.current || !(direction === 'next' ? canNext : canPrev)) {
      return;
    }
    const width = node?.clientWidth ?? 0;
    settle((direction === 'next' ? nextSign : -nextSign) * width, 0, direction === 'next' ? onNext : onPrev);
  };

  const onTouchStart = (event: TouchEvent) => {
    if (!enabled || settling.current || event.touches.length !== 1) {
      if (gesture.current) {
        gesture.current = null;
        settle(0, 0);
      }
      return;
    }
    const {clientX, clientY} = event.touches[0];
    gesture.current = {startX: clientX, startY: clientY, lastX: clientX, lastTime: event.timeStamp, velocity: 0, axis: null};
    if (trackRef.current) {
      trackRef.current.style.transition = 'none';
    }
  };

  const onTouchMove = (event: TouchEvent) => {
    const current = gesture.current;
    const node = trackRef.current;
    if (!current || !node || event.touches.length !== 1) {
      return;
    }
    const {clientX, clientY} = event.touches[0];
    const deltaX = clientX - current.startX;
    if (!current.axis) {
      if (Math.abs(deltaX) < AXIS_LOCK_PX && Math.abs(clientY - current.startY) < AXIS_LOCK_PX) {
        return;
      }
      current.axis = Math.abs(deltaX) > Math.abs(clientY - current.startY) ? 'x' : 'y';
    }
    if (current.axis !== 'x') {
      return;
    }
    const elapsed = event.timeStamp - current.lastTime;
    if (elapsed > 0) {
      current.velocity = (clientX - current.lastX) / elapsed;
    }
    current.lastX = clientX;
    current.lastTime = event.timeStamp;
    const towardNext = Math.sign(deltaX) === nextSign;
    const blocked = towardNext ? !canNext : !canPrev;
    node.style.transform = `translate3d(${blocked ? deltaX * EDGE_RESISTANCE : deltaX}px, 0, 0)`;
  };

  const onTouchEnd = () => {
    const current = gesture.current;
    gesture.current = null;
    if (current?.axis !== 'x') {
      return;
    }
    swipedAt.current = performance.now();
    const width = trackRef.current?.clientWidth ?? 0;
    const deltaX = current.lastX - current.startX;
    const flick = Math.abs(current.velocity) > COMMIT_VELOCITY && Math.sign(current.velocity) === Math.sign(deltaX);
    const committed = flick || Math.abs(deltaX) > width * COMMIT_DISTANCE_RATIO;
    const speed = Math.abs(current.velocity);
    if (committed && Math.sign(deltaX) === nextSign && canNext) {
      settle(nextSign * width, speed, onNext);
    } else if (committed && Math.sign(deltaX) === -nextSign && canPrev) {
      settle(-nextSign * width, speed, onPrev);
    } else {
      settle(0, speed);
    }
  };

  const consumeSwipe = () => {
    const wasSwipe = performance.now() - swipedAt.current < SWIPE_CLICK_WINDOW_MS;
    swipedAt.current = 0;
    return wasSwipe;
  };

  const slideStyle = (position: number): CSSProperties => ({transform: `translate3d(${position * -nextSign * 100}%, 0, 0)`});

  return {
    trackRef,
    go,
    consumeSwipe,
    slideStyle,
    handlers: {onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd},
  };
}
